import { FC, useCallback, useEffect, useState } from 'react';
import { getStateByZip } from 'shared/services/geo';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden'; // FIXME: deprecated in favour of useMediaQuery() hook
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import includes from 'lodash/includes';

import Layout from 'containers/Layout';

import Sidebar from './components/Sidebar';
import Body from './components/Body';

import EmptyState from 'components/EmptyState';
import RequestCarButton from './components/RequestCarButton';
import RequestStateButton from './components/RequestStateButton';

import Breadcrumbs from 'components/molecules/Breadcrumbs';
import StripesBackground from 'components/atoms/StripesBackground';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import { StyledFontAwesomeIconMui5 } from '../../components/atoms/FontAwesomeIcon';
import { StyledButtonMui5 } from '../../components/atoms/StyledButton';
import { DealsContainerProps } from 'pages/deals';
import { allowedStates } from 'data/allowedStates';
import DealsToolbar from './components/DealsToolbar';
import { useScreenSize } from 'hooks/useScreenSize';
import {
  RebatesCategoriesDialog,
  useRebatesCategoriesDialog,
} from 'components/RebatesCategoriesDialog';
import {
  confirmationDialogOpenStateAtom,
  localStorage,
  rebatesCategoriesDialogAtom,
  signUpPopupEnabledAtom,
  signUpPopupOpenAtom,
  userAtom,
} from 'recoil/atoms';
import { grey } from 'theme/colors';
import { DividerMui5 } from 'components/atoms/Divider';
import { SignUpPopup } from 'components/organisms/SignUpPopup';
import { BRAND_NAME } from 'shared/utils/constants';
import SeoLinks from './components/SeoLinks';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  getElementPositionPercentage,
  useHandleScroll,
} from '../../hooks/useHandleScroll';
import { ENVIRONMENT } from '../../shared/config';
import { TopSearchBar } from '../../components/organisms/TopSearch';

const Section = styled('section')(({ theme }) => ({
  paddingBottom: theme.spacing(6),
}));

const useRebatesCategoriesDialogOnce = () => {
  const { openRebatesCategoriesDialog } = useRebatesCategoriesDialog({});

  useEffect(() => {
    const wasOpened = localStorage.get('pop_rebates_categories_dialog');
    if (!wasOpened) {
      openRebatesCategoriesDialog();
      localStorage.set('pop_rebates_categories_dialog', 1);
    }
  }, [openRebatesCategoriesDialog]);
};

const StyledStripesBackground = styled(StripesBackground)(
  ({ theme }) => `
  ${theme.breakpoints.down('md')} {
    background-color: white;
    background-image: none;
  }`,
);

const ContentTitle = styled(Typography)(() => ({
  '&.MuiTypography-root': {
    fontSize: '1.25rem',
  },
}));

export const DealsContainer: FC<DealsContainerProps> = ({
  auctions,
  featuredAuctions,
  auctionsCount,
  zipcode,
  dynamicFilters,
  searchFilters,
  perPage,
  isMobileView,
}) => {
  const user = useRecoilValue(userAtom);
  const showSignUpPopup = process.env.ENVIRONMENT === ENVIRONMENT.PRODUCTION;
  const [signUpPopupEnabled, setSignUpPopupEnabled] = useRecoilState(
    signUpPopupEnabledAtom,
  );
  const setSignUpPopupOpenAtom = useSetRecoilState(signUpPopupOpenAtom);
  const confirmationDialogOpenState = useRecoilValue(
    confirmationDialogOpenStateAtom,
  );
  const rebatesCategoriesDialogState = useRecoilValue(
    rebatesCategoriesDialogAtom,
  );

  const screenSize = useScreenSize();
  const getScreenHeight = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }
    let element: Element;
    if (screenSize.isExtraSmallScreen) {
      element = document.querySelector('.js-offer-card-0-3');
    } else if (screenSize.isMediumScreen) {
      element = document.querySelector('.js-offer-card-0-7');
    } else {
      element = document.querySelector('.js-offer-card-0-11');
    }
    if (!element) {
      return;
    }
    return getElementPositionPercentage(element).topPercentage;
  }, [
    auctions?.length,
    screenSize.isExtraSmallScreen,
    screenSize.isMediumScreen,
  ]);
  const handleScroll = () => {
    if (user || !showSignUpPopup) {
      if (signUpPopupEnabled) {
        setSignUpPopupEnabled(false);
      }
      return;
    }
    if (!confirmationDialogOpenState && !rebatesCategoriesDialogState?.isOpen) {
      setSignUpPopupEnabled(true);
      setSignUpPopupOpenAtom(true);
    }
  };
  useHandleScroll(
    getScreenHeight() ?? 150, // fallback to unrealistic scroll percentage to prevent slimy scroll behaviour in some browsers
    () => {
      handleScroll();
      return false;
    },
    true,
  );

  const { isSmallScreen, isExtraSmallScreen } = useScreenSize();

  const isCompact = isMobileView || isExtraSmallScreen || isSmallScreen;

  const [isStateAvailable, setStateAvailability] = useState(true);
  const [isSidebarDialogOpened, setSidebarDialogOpened] = useState(false);

  useEffect(() => {
    if (zipcode) {
      setStateAvailability(
        includes(allowedStates, getStateByZip(String(zipcode)).state),
      );
    } else {
      setStateAvailability(true);
    }
  }, [zipcode]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [searchFilters, dynamicFilters]);

  useRebatesCategoriesDialogOnce();

  const toggleSidebar = () => setSidebarDialogOpened(!isSidebarDialogOpened);

  // FIXME: isCompact hook does not work properly on changes
  const gridColumnSpacing = isCompact ? 0 : 2;

  return (
    <Layout>
      <RebatesCategoriesDialog />
      <SignUpPopup />
      <StyledStripesBackground>
        <Container maxWidth='lg'>
          <TopSearchBar />
        </Container>
        <Container className='js-search-page' maxWidth='lg'>
          <Hidden implementation='css' mdDown>
            <Breadcrumbs
              sxProps={(theme) => ({ margin: theme.spacing(2, 0) })}
              items={[{ name: 'Home', href: '/' }, { name: 'Deals' }]}
            />
          </Hidden>
          <Grid container spacing={gridColumnSpacing}>
            <Grid item xs={12} sm={12} md={3}>
              <Hidden implementation='css' mdDown>
                <Sidebar
                  dynamicFilters={dynamicFilters}
                  searchFilters={searchFilters}
                  isCompact={false}
                />
              </Hidden>
            </Grid>
            <Grid item xs={12} sm={12} md={9}>
              {isStateAvailable && auctions.length > 0 && (
                <Body
                  auctions={auctions}
                  featuredAuctions={featuredAuctions}
                  auctionsCount={auctionsCount}
                  perPage={perPage}
                  onOpenFiltersRequest={() => toggleSidebar()}
                  zipcode={zipcode}
                />
              )}
              {!isStateAvailable && (
                <>
                  <DealsToolbar
                    auctionsCount={0}
                    onFiltersClick={() => toggleSidebar()}
                    zipcode={zipcode}
                  />
                  <EmptyState
                    title='We are not available in your location'
                    preSubtitle='Currently we are operating in NJ, NY, PA, CT, MD and CA'
                    subtitle={`Please notify me when ${BRAND_NAME} comes to my location`}
                  >
                    <RequestStateButton />
                  </EmptyState>
                </>
              )}
              {isStateAvailable && auctions.length === 0 && (
                <>
                  <DealsToolbar
                    auctionsCount={0}
                    onFiltersClick={() => toggleSidebar()}
                    zipcode={zipcode}
                  />
                  <EmptyState
                    title='No results found 🔍'
                    subtitle='Almost yours! That ride’s just out of reach, but no worries! Simply adjust your search filters or get a customized offer from our Deal Hunter, tailored to fit your preferences and budget.'
                  >
                    <RequestCarButton />
                  </EmptyState>
                </>
              )}
            </Grid>
          </Grid>
        </Container>
        <Box
          color={grey[700]}
          mt={4}
          justifyContent='center'
          alignItems='center'
          display='flex'
          flexDirection='column'
        >
          <Container>
            <Section>
              <SeoLinks />
            </Section>
          </Container>
          <Box mr={4} ml={4}>
            <Container>
              <Section>
                <ContentTitle variant='h2' gutterBottom align='center'>
                  Unlock Exclusive Savings in Our Best Deals Section!
                </ContentTitle>
                <DividerMui5 />
                <Typography
                  variant='body2'
                  gutterBottom
                  color='textSecondary'
                  align='center'
                >
                  Discover unbeatable savings with Lease Bandit&apos;s Best
                  Deals Section! Explore exclusive offers on both luxury and
                  affordable car leases and financing options. Whether
                  you&apos;re seeking high-end sophistication or exceptional
                  value, our curated selection provides flexible solutions to
                  fit every budget. Experience extraordinary savings and drive
                  your dream car today—where luxury and affordability meet!
                </Typography>
              </Section>
            </Container>
          </Box>
        </Box>
      </StyledStripesBackground>
      {isCompact && (
        <>
          <Dialog open={isSidebarDialogOpened} fullScreen fullWidth>
            <AppBar color='default'>
              <Toolbar>
                <Box display='flex' justifyContent='space-between' width='100%'>
                  <Box display='flex' alignItems={'center'}>
                    <StyledButtonMui5
                      variant={'text'}
                      onClick={toggleSidebar}
                      sx={{ minWidth: '32px' }}
                    >
                      <StyledFontAwesomeIconMui5 icon={faArrowLeft} size='lg' />
                      <Typography variant='body1'>Filters</Typography>
                    </StyledButtonMui5>
                  </Box>
                  <StyledButtonMui5
                    variant='contained'
                    color={'secondary'}
                    onClick={toggleSidebar}
                  >
                    Show results ({auctionsCount})
                  </StyledButtonMui5>
                </Box>
              </Toolbar>
            </AppBar>
            <Box mt={7}>
              <Sidebar
                dynamicFilters={dynamicFilters}
                searchFilters={searchFilters}
                isCompact={true}
              />
            </Box>
          </Dialog>
        </>
      )}
    </Layout>
  );
};
